import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { ProcurementPlanAutoResubmitCategory } from '@ebalance/proto/bfftypes/v1/procurement_plan_auto_resubmit_category_pb'
import {
  type ProcurementPlanAutoResubmitSetting,
  ProcurementPlanAutoResubmitSettingSchema,
} from '@ebalance/proto/bffuser/v1/plan_auto_resubmit_setting_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  Grid,
  Switch,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { Modal } from '~/components/common/Modal'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { useAutoResubmitSettingSave } from '~/pages/ProcurementPlanList/hooks/useAutoResubmitSettingSave'
import { useListProcurementPlanAutoResubmitSettings } from '~/pages/ProcurementPlanList/hooks/useListProcurementPlanAutoResubmitSettings'
import {
  AUTO_RESUBMIT_CATEGORY_LABEL_KEY,
  autoResubmitUnitKey,
} from '~/pages/ProcurementPlanList/models/autoResubmitSetting'

const NS = 'page:procurementPlanList.autoResubmitSettings'

type PendingChange = {
  /**
   * 確認を開いた時点の単位 (updatedAt を含む)。 確認の間に一覧が読み直されても、
   * この updatedAt を楽観ロックの基準に送る (間の先行変更を Aborted で検出するため)
   */
  unit: ProcurementPlanAutoResubmitSetting
  enabled: boolean
}

/**
 * 取得中に並べる 20 単位 (server の固定順と同じ AreaCode 順 × 需要 BG → 自己託送 BG)。
 * 値は使わない (取得中はスイッチを伏せて操作させない。 AC-PAR-010)
 */
const PENDING_UNITS: readonly ProcurementPlanAutoResubmitSetting[] =
  AREA_CODE_OPTIONS.flatMap((areaCode) =>
    [
      ProcurementPlanAutoResubmitCategory.DEMAND_BG,
      ProcurementPlanAutoResubmitCategory.SELF_WHEELING_BG,
    ].map((category) =>
      create(ProcurementPlanAutoResubmitSettingSchema, { areaCode, category }),
    ),
  )

type Props = {
  onClose: () => void
}

/**
 * 「自動再提出の設定」モーダル (AC-PAR-001 / 002 / 010 / 011)。
 * design: `design/plan/procurement.html` #par-settings-modal / #par-confirm-modal、
 * 振る舞いは `design/js/plan-auto-resubmit.js`。
 *
 * 開いている間だけ mount する (取得の cache を開くたびに捨てるため。
 * `useListProcurementPlanAutoResubmitSettings` の gcTime 参照)。
 * 業務エリアで行を絞らず 20 単位すべてを出す (業務エリア外の単位がオンのまま見えなく
 * なるのを避ける)。
 */
export const AutoResubmitSettingsModal = ({
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isError, isFetching, refetch } =
    useListProcurementPlanAutoResubmitSettings()
  const { savingKeys, save } = useAutoResubmitSettingSave()
  const [pending, setPending] = useState<PendingChange | undefined>()

  const categoryLabel = (
    unit: Pick<ProcurementPlanAutoResubmitSetting, 'category'>,
  ): string => t(AUTO_RESUBMIT_CATEGORY_LABEL_KEY[unit.category])
  const valueLabel = (enabled: boolean): string =>
    t(enabled ? `${NS}.value.on` : `${NS}.value.off`)

  const handleConfirm = (): void => {
    if (pending === undefined) return
    setPending(undefined)
    if (savingKeys.has(autoResubmitUnitKey(pending.unit))) return
    save(pending.unit, pending.enabled)
  }

  // 使えない (feature flag OFF) と返った時は単位が無いので、 取得失敗と同じ表示に
  // する (`ProcurementPlanDetail/NoSubmissionRecord.tsx` と同じ扱い)
  const settings = data?.featureEnabled === true ? data.settings : undefined
  const isUnavailable = data !== undefined && !data.featureEnabled
  // 取得中・取得失敗の間は、 取得済みの値が cache に残っていても未確定として扱う
  // (取得済みの後の背景の再取得〈通信の復帰 等〉を含む。 AC-PAR-010)。
  // 取得失敗 (共通の再試行の後) は表を隠して「再読み込み」を出し、 押した後の
  // 取得中は取得中の表示に戻す
  const showLoadError = (isError || isUnavailable) && !isFetching
  const isLoadingSettings = settings === undefined || isFetching

  return (
    <>
      <Modal
        footer={
          <TertiaryButton onClick={onClose}>
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen
        onClose={onClose}
        title={t(`${NS}.title`)}
      >
        <Flex flexDirection="column" gap={3}>
          <Text>{t(`${NS}.description`)}</Text>
          {showLoadError ? (
            <Flex alignItems="flex-start" flexDirection="column" gap={3}>
              <Alert status="error" title={t(`${NS}.loadFailed`)} />
              <TertiaryButton
                onClick={() => {
                  void refetch()
                }}
                size="md"
              >
                {t(`${NS}.reload`)}
              </TertiaryButton>
            </Flex>
          ) : (
            <TableRoot
              fontSize="md"
              aria-busy={isLoadingSettings}
              aria-label={t(`${NS}.title`)}
              size="sm"
              style={{ width: '100%', borderCollapse: 'collapse' }}
            >
              <TableHeader>
                <TableRow>
                  <TableColumnHeader textAlign="center">
                    {t(`${NS}.column.area`)}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t(`${NS}.column.category`)}
                  </TableColumnHeader>
                  <TableColumnHeader>
                    {t(`${NS}.column.autoResubmit`)}
                  </TableColumnHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(settings ?? PENDING_UNITS).map((unit) => {
                  const key = autoResubmitUnitKey(unit)
                  const area = convertAreaLabel(unit.areaCode)
                  const category = categoryLabel(unit)
                  return (
                    <TableRow key={key}>
                      <TableCell textAlign="center">{area}</TableCell>
                      <TableCell>{category}</TableCell>
                      <TableCell>
                        <Flex alignItems="center" gap={2}>
                          <Switch
                            aria-label={t(`${NS}.switchLabel`, {
                              area,
                              category,
                            })}
                            // 保存済みの値に結ぶ。 切替は確認の確定後に保存し、
                            // 保存が成功した値だけを表示する (AC-PAR-011)
                            // 取得中は design どおり行を先に出し、 スイッチは未チェック・
                            // 操作不可、 オン/オフの文言は伏せる (既定のオフとみなさない)
                            checked={!isLoadingSettings && unit.enabled}
                            disabled={isLoadingSettings || savingKeys.has(key)}
                            // DS (Ark) の Switch は controlled でも隠し input の
                            // checked を DOM 上で反転させ、 値が変わらないと戻さない。
                            // キャンセル・保存失敗の後に読み上げが「オン」のまま残るので、
                            // input の click を取り消して確認だけを開く
                            onClickCapture={(e: React.MouseEvent) => {
                              if (!(e.target instanceof HTMLInputElement)) {
                                return
                              }
                              e.preventDefault()
                              if (isLoadingSettings || pending !== undefined) {
                                return
                              }
                              setPending({ unit, enabled: !unit.enabled })
                            }}
                          />
                          {isLoadingSettings ? null : (
                            <Text>{valueLabel(unit.enabled)}</Text>
                          )}
                        </Flex>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </TableRoot>
          )}
        </Flex>
      </Modal>

      <ConfirmDialog
        body={
          pending === undefined ? null : (
            <Flex flexDirection="column" gap={3}>
              <Text>
                {t(
                  pending.enabled
                    ? `${NS}.confirm.startBody`
                    : `${NS}.confirm.stopBody`,
                )}
              </Text>
              <Grid
                as="dl"
                gridRowGap={1}
                gridColumnGap={4}
                gridTemplateColumns="max-content 1fr"
                margin={0}
              >
                {[
                  [
                    t(`${NS}.confirm.planType`),
                    t(`${NS}.confirm.planTypeValue`),
                  ],
                  [
                    t(`${NS}.confirm.area`),
                    convertAreaLabel(pending.unit.areaCode),
                  ],
                  [t(`${NS}.confirm.category`), categoryLabel(pending.unit)],
                  [t(`${NS}.confirm.newValue`), valueLabel(pending.enabled)],
                ].map(([term, description]) => (
                  <div key={term} style={{ display: 'contents' }}>
                    <Text as="dt" color="Text.lowEmphasis">
                      {term}
                    </Text>
                    <Text as="dd" margin={0}>
                      {description}
                    </Text>
                  </div>
                ))}
              </Grid>
            </Flex>
          )
        }
        confirmLabel={t(
          pending?.enabled === true
            ? `${NS}.confirm.startConfirm`
            : `${NS}.confirm.stopConfirm`,
        )}
        isOpen={pending !== undefined}
        onCancel={() => {
          setPending(undefined)
        }}
        onConfirm={handleConfirm}
        title={t(
          pending?.enabled === true
            ? `${NS}.confirm.startTitle`
            : `${NS}.confirm.stopTitle`,
        )}
      />
    </>
  )
}
